// Librairies
import { ChangeDetectionStrategy, Component, Input, OnChanges } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';

// Interfaces
export type ThumbnailKind = 'line' | 'area' | 'columnStacked' | 'columnGrouped' | 'bar' | 'mix' | 'distribution' | 'pie' | 'doughnut'
  | 'scatter' | 'table' | 'kpi' | 'heatmap' | 'bubbles' | 'insight' | 'icon';

export type ThumbnailFamily = 'trend' | 'compare' | 'part' | 'data' | 'map' | 'media';

export interface ThumbnailOptions {
  forecast?: boolean;   // the projection: dashed, with its band
  byProject?: boolean;  // one colour per project
  trend?: boolean;      // KPI: the ▲ of the comparison
}

// One SVG shape; cls: c1 | c2 | c3 (series), grid, band, dash, hole, good, label.
export interface ThumbnailShape {
  tag: 'rect' | 'path' | 'circle' | 'text';
  cls: string;
  x?: number; y?: number; w?: number; h?: number; rx?: number; r?: number;
  d?: string; text?: string; opacity?: number;
}

// Variables
const KINDS: Record<string, ThumbnailKind> = {
  discreteLine: 'line', continiousLine: 'line', discreteArea: 'area', continiousArea: 'area',
  columnStacked: 'columnStacked', columnGrouped: 'columnGrouped', barStacked: 'bar', barGrouped: 'bar',
  mixStacked: 'mix', mixGrouped: 'mix', barDistribution: 'distribution', pie: 'pie', doughnut: 'doughnut',
  gridScatter: 'scatter', geoHeatmap: 'bubbles', geoScatter: 'bubbles',
};

const TYPE_KINDS: Record<string, ThumbnailKind> = {
  block: 'kpi', table: 'table', heatmap: 'heatmap', projectMap: 'bubbles', map: 'bubbles', insight: 'insight',
};

const FAMILIES: Record<ThumbnailKind, ThumbnailFamily> = {
  line: 'trend', area: 'trend', insight: 'trend',
  columnStacked: 'compare', columnGrouped: 'compare', bar: 'compare', mix: 'compare',
  distribution: 'part', pie: 'part', doughnut: 'part',
  table: 'data', kpi: 'data',
  scatter: 'map', heatmap: 'map', bubbles: 'map',
  icon: 'media',
};

const ICONS: Record<string, string> = {
  image: 'image', video: 'videocam', weather: 'wb_sunny', forge: '3d_rotation', planning3D: 'view_in_ar',
};

const SERIES = ['c1', 'c2', 'c3'];
const HISTORY: [number, number][] = [[8, 54], [26, 44], [44, 48], [62, 34], [80, 38]];
const PROJECTION: [number, number][] = [[80, 38], [96, 28], [112, 24]];
const COLUMNS = [22, 34, 28, 42, 36, 48];

// Classes
export function thumbnailKind(type?: string, graphType?: string): ThumbnailKind {
  if (type === 'chart' || type === 'map')
    return KINDS[graphType ?? ''] ?? 'columnStacked';

  return TYPE_KINDS[type ?? ''] ?? 'icon';
}

export function thumbnailFamily(kind: ThumbnailKind): ThumbnailFamily {
  return FAMILIES[kind];
}

export function thumbnailIcon(type?: string): string {
  return ICONS[type ?? ''] ?? 'widgets';
}

const path = (points: [number, number][]): string => points.map(([x, y], i) => `${i ? 'L' : 'M'}${x} ${y}`).join(' ');

// The projection band, around the dashed line.
const band = (): string =>
  `${path(PROJECTION.map(([x, y], i) => [x, y - i * 5] as [number, number]))} ` +
  PROJECTION.slice().reverse().map(([x, y], i) => `L${x} ${y + (PROJECTION.length - 1 - i) * 5}`).join(' ') + ' Z';

const slice = (from: number, to: number, r: number): string => {
  const point = (turn: number) => [60 + r * Math.sin(turn * 2 * Math.PI), 36 - r * Math.cos(turn * 2 * Math.PI)];
  const [x0, y0] = point(from), [x1, y1] = point(to);

  return `M60 36 L${x0!.toFixed(1)} ${y0!.toFixed(1)} A${r} ${r} 0 ${to - from > 0.5 ? 1 : 0} 1 ${x1!.toFixed(1)} ${y1!.toFixed(1)} Z`;
};

function lines(options: ThumbnailOptions, area: boolean): ThumbnailShape[] {
  const shapes: ThumbnailShape[] = [];
  const offsets = options.byProject ? [0, 10, -8] : [0];

  offsets.forEach((offset, i) => {
    const history = HISTORY.map(([x, y]) => [x, y + offset] as [number, number]);

    if (area && i === 0)
      shapes.push({ tag: 'path', cls: 'c1 soft', d: `${path(history)} L80 64 L8 64 Z` });

    shapes.push({ tag: 'path', cls: `${SERIES[i]} stroke`, d: path(history) });
  });

  if (options.forecast) {
    shapes.push({ tag: 'path', cls: 'band', d: band() }, { tag: 'path', cls: 'c1 stroke dash', d: path(PROJECTION) });
  }

  return shapes;
}

function columns(options: ThumbnailOptions, grouped: boolean): ThumbnailShape[] {
  const segments = options.byProject ? 3 : 2;

  return COLUMNS.flatMap((height, i) => {
    const projected = options.forecast && i >= COLUMNS.length - 2;
    const x = 10 + i * 17;

    if (grouped) {
      return Array.from({ length: segments }, (_, s) => ({
        tag: 'rect', cls: projected ? `${SERIES[s]} dash outline` : SERIES[s]!,
        x: x + s * (12 / segments), y: 64 - height * (1 - s * 0.25), w: 12 / segments - 1, h: height * (1 - s * 0.25), rx: 1,
      }) as ThumbnailShape);
    }

    let top = 64;

    return Array.from({ length: segments }, (_, s) => {
      const part = height / segments;
      top -= part;
      return { tag: 'rect', cls: projected ? `${SERIES[s]} dash outline` : SERIES[s]!, x, y: top, w: 11, h: part, rx: 1 } as ThumbnailShape;
    });
  });
}

// The static drawing of a widget type: simple shapes, no data.
export function thumbnailShapes(kind: ThumbnailKind, options: ThumbnailOptions = {}): ThumbnailShape[] {
  const grid: ThumbnailShape = { tag: 'rect', cls: 'grid', x: 6, y: 64, w: 108, h: 1 };

  switch (kind) {
    case 'line': return [grid, ...lines(options, false)];
    case 'area': return [grid, ...lines(options, true)];
    case 'columnStacked': return [grid, ...columns(options, false)];
    case 'columnGrouped': return [grid, ...columns(options, true)];
    case 'mix': return [grid, ...columns({ ...options, byProject: false }, false), { tag: 'path', cls: 'c3 stroke', d: path(HISTORY.map(([x, y]) => [x + 8, y - 14])) }];
    case 'bar':
      return [70, 56, 42, 28].map((width, i) => ({
        tag: 'rect', cls: options.byProject ? SERIES[i % 3]! : 'c1', x: 10, y: 10 + i * 14, w: options.forecast && i === 3 ? width : width + 30, h: 8, rx: 2,
      }) as ThumbnailShape);
    case 'distribution':
      return [[0.5, 0.3], [0.35, 0.4], [0.6, 0.25]].flatMap(([a, b], i) => [
        { tag: 'rect', cls: 'c1', x: 10, y: 12 + i * 18, w: 100 * a!, h: 10 },
        { tag: 'rect', cls: 'c2', x: 10 + 100 * a!, y: 12 + i * 18, w: 100 * b!, h: 10 },
        { tag: 'rect', cls: 'c3', x: 10 + 100 * (a! + b!), y: 12 + i * 18, w: 100 * (1 - a! - b!), h: 10 },
      ] as ThumbnailShape[]);
    case 'pie':
    case 'doughnut': {
      const shapes: ThumbnailShape[] = [
        { tag: 'path', cls: 'c1', d: slice(0, 0.45, 26) },
        { tag: 'path', cls: 'c2', d: slice(0.45, 0.75, 26) },
        { tag: 'path', cls: 'c3', d: slice(0.75, 0.999, 26) },
      ];
      return kind === 'doughnut' ? [...shapes, { tag: 'circle', cls: 'hole', x: 60, y: 36, r: 14 }] : shapes;
    }
    case 'scatter':
      return [grid, ...[[18, 50, 3], [30, 38, 4], [44, 44, 3], [58, 28, 5], [72, 34, 3], [86, 20, 4], [100, 26, 3]]
        .map(([x, y, r], i) => ({ tag: 'circle', cls: options.byProject ? SERIES[i % 3]! : 'c1', x, y, r }) as ThumbnailShape)];
    case 'table':
      return [
        { tag: 'rect', cls: 'grid', x: 8, y: 10, w: 104, h: 8, rx: 2 },
        ...[0, 1, 2].flatMap(i => [
          { tag: 'rect', cls: 'label', x: 10, y: 26 + i * 14, w: 46, h: 4, rx: 2 },
          { tag: 'rect', cls: SERIES[i]!, x: 78, y: 24 + i * 14, w: 26, h: 8, rx: 4 },
        ] as ThumbnailShape[]),
      ];
    case 'kpi':
      return [
        { tag: 'text', cls: 'figure', x: 14, y: 46, text: '128' },
        { tag: 'rect', cls: 'label', x: 14, y: 54, w: 44, h: 4, rx: 2 },
        ...(options.trend ? [
          { tag: 'path', cls: 'good', d: 'M86 40 L94 28 L102 40 Z' },
          { tag: 'rect', cls: 'good soft', x: 82, y: 46, w: 24, h: 7, rx: 3 },
        ] as ThumbnailShape[] : []),
      ];
    case 'heatmap':
      return Array.from({ length: 32 }, (_, i) => ({
        tag: 'rect', cls: 'c1', x: 10 + (i % 8) * 13, y: 10 + Math.floor(i / 8) * 13, w: 11, h: 11, rx: 2,
        opacity: 0.2 + ((i * 7) % 10) / 12,
      }) as ThumbnailShape);
    case 'bubbles':
      return [
        { tag: 'rect', cls: 'grid', x: 8, y: 8, w: 104, h: 56, rx: 6 },
        ...[[30, 30, 8], [58, 44, 5], [78, 24, 10], [96, 46, 4], [44, 52, 3]]
          .map(([x, y, r], i) => ({ tag: 'circle', cls: options.byProject ? SERIES[i % 3]! : 'c1', x, y, r, opacity: 0.8 }) as ThumbnailShape),
      ];
    case 'insight':
      return [
        { tag: 'text', cls: 'figure small', x: 8, y: 30, text: '45 j' },
        { tag: 'rect', cls: 'c1', x: 8, y: 40, w: 30, h: 4, rx: 2 },
        { tag: 'rect', cls: 'c2', x: 8, y: 50, w: 20, h: 4, rx: 2 },
        { tag: 'path', cls: 'c1 stroke', d: 'M50 54 L64 46 L78 50 L92 36' },
        ...(options.forecast ? [
          { tag: 'path', cls: 'band', d: 'M92 36 L112 22 L112 34 Z' },
          { tag: 'path', cls: 'c1 stroke dash', d: 'M92 36 L112 28' },
        ] as ThumbnailShape[] : []),
      ];
    default:
      return [];
  }
}

// Small static illustration of a widget for the catalog: a tinted card and a drawing of its type.
@Component({
  selector: 'app-widget-thumbnail',
  standalone: true,
  imports: [
    MatIconModule
  ],
  templateUrl: './widget-thumbnail.component.html',
  styleUrl: './widget-thumbnail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class WidgetThumbnailComponent implements OnChanges {
  // Variables
  @Input() type?: string;
  @Input() graphType?: string;
  @Input() forecast: boolean = false;
  @Input() byProject: boolean = false;
  @Input() trend: boolean = false;

  kind: ThumbnailKind = 'icon';
  family: ThumbnailFamily = 'media';
  icon: string = '';
  shapes: ThumbnailShape[] = [];

  ngOnChanges(): void {
    this.kind = thumbnailKind(this.type, this.graphType);
    this.family = thumbnailFamily(this.kind);
    this.icon = thumbnailIcon(this.type);
    this.shapes = thumbnailShapes(this.kind, { forecast: this.forecast, byProject: this.byProject, trend: this.trend });
  }
}
